import { AuthorizedRoute } from '@/components/AuthorizedRoute/AuthorizedRoute'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { ApiariesProvider } from '@/contexts/useApiaries'
import {
  ClusterConfigRequestProvider,
  type ClusterUpdateWithOptionalProductId,
  useClusterConfigRequest,
} from '@/contexts/useConfigCreateRequest'
import { TemplatesProvider } from '@/contexts/useTemplates'
import { usePostCluster } from '@/generated/http-clients/v3ApiComponents'
import type { ClusterUpdate } from '@/generated/http-clients/v3ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
  toaster,
} from '@/hooks/useToast/toastUtils'
import { clustersRootRoute } from '@/router/rootRoutes'
import { debugModeAvailable } from '@/scripts/environments'
import {
  Box,
  Button,
  HStack,
  Heading,
  Icon,
  IconButton,
  Steps,
  Tabs,
  Tag,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { Check, X } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import YAML from 'yaml'
import { clusterConfigTableRoute } from '../../pages/ClusterConfigTable/ClusterConfigTable'
import { NewClusterStep1System } from './NewClusterStep1System'
import {
  ExtensionType,
  NewClusterStep2Features,
} from './NewClusterStep2Features'
import { NewClusterStep3Overrides } from './NewClusterStep3Overrides'
import { NewClusterStep4Review } from './NewClusterStep4Review'
import { StepNavigator, StepNavigatorItemStatus } from './StepNavigator'
import { clusterStep1Schema } from './clusterStep1Schema'

export const newClusterConfigRoute = createRoute({
  getParentRoute: () => clustersRootRoute,
  path: 'new-hive-wizard',
  component: () => (
    <AuthorizedRoute>
      <ApiariesProvider>
        <ClusterConfigRequestProvider>
          <NewClusterConfig />
        </ClusterConfigRequestProvider>
      </ApiariesProvider>
    </AuthorizedRoute>
  ),
})

enum StepIndex {
  STEP1_SYSTEM = 0,
  STEP2_EXTENSIONS = 1,
  STEP3_OVERRIDES = 2,
  STEP4_REVIEW = 3,
}

const DELAY_BEFORE_FOCUS = 100

export function NewClusterConfig() {
  const { t } = useTranslation('translation', { useSuspense: false })
  const navigate = useNavigate()

  const createCluster = usePostCluster({})

  const stepOneRef = React.useRef<{
    validateForm: () => boolean
  } | null>(null)

  const step3OverridesRef = React.useRef<{
    focus: () => void
  } | null>(null)

  const [configIsValid, setConfigIsValid] = React.useState(false)
  const [validationError, setValidationError] = React.useState<string | null>(
    '',
  )

  const [currentStep, setCurrentStep] = React.useState(0)

  const { request, onChange } = useClusterConfigRequest()
  // configUpdateBody uses ClusterUpdateWithOptionalProductId since the form
  // starts with no product selected. The actual ClusterUpdate is built when submitting.
  const [configUpdateBody, setConfigUpdateBody] =
    React.useState<ClusterUpdateWithOptionalProductId>(request)

  const [showDebug, setShowDebug] = React.useState(false)
  const [pendingFocus, setPendingFocus] = React.useState<{
    step: number
    field: string
  } | null>(null)

  const [focusOverrideEditor, setFocusOverrideEditor] = React.useState(false)

  // update the config update payload body for variables starting with an _
  // e.g. _kafkaExtension -> copy over to request.customer.extensions.kafka
  React.useEffect(() => {
    const {
      _organizationId, // ignore this one, it's used for the API call
      _deploymentLabel,
      _kafkaExtension,
      _kinesisExtension,
      _clusterName,
      _overrideYaml,
      ...baseRequest
    } = request

    const customer = baseRequest.customer as NonNullable<
      typeof baseRequest.customer
    >
    const extensions = { ...(customer.extensions ?? {}) }
    const kafka = _kafkaExtension
    const kinesis = _kinesisExtension

    let override: (typeof request)['override'] = undefined
    if (_overrideYaml) {
      try {
        override = YAML.parse(_overrideYaml)
      } catch {
        /* fail silently */
      }
    }

    // Inject form fields into the proper nested structure
    // product_id is optional here since the form may not have a product selected yet
    const updatedRequest: ClusterUpdateWithOptionalProductId = {
      ...baseRequest,
      system: {
        global: {
          ...baseRequest.system.global,
          name: _clusterName,
          deployment_label: _deploymentLabel ?? '',
        },
      },
      customer: {
        ...customer,
        extensions: {
          ...extensions,
          kafka,
          kinesis,
        },
      },
      override,
    }

    setConfigUpdateBody(updatedRequest)
  }, [request])

  const yamlRequest = React.useMemo(() => {
    try {
      const { system, override, customer, template_id, product_id, changelog } =
        configUpdateBody

      const yaml = `${YAML.stringify({ system })}\
${override ? YAML.stringify({ override }) : ''}\
${YAML.stringify({ customer })}\
template_id: ${template_id}\n
${product_id ? `product_id: ${product_id}\n` : ''}
changelog: ${changelog}`.replace(/\n+/g, '\n')

      if (!request._organizationId) {
        setConfigIsValid(false)
        setValidationError(t('errors.org.empty'))
        return yaml
      }

      if (!template_id) {
        setConfigIsValid(false)
        setValidationError(t('errors.templateId.empty'))
        return yaml
      }

      setConfigIsValid(true)
      setValidationError('')

      return yaml
    } catch (_error) {
      // fail silently here
      setValidationError('YAML.stringify failed')
      setConfigIsValid(false)
    }
  }, [configUpdateBody, request._organizationId, t])

  function submit() {
    if (!yamlRequest) {
      // TODO: look at integrating a YAML validator to display errors. A schema would need to be provided by the backend via an API
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('errors.invalidYaml'),
      })
      return
    }

    // Ensure product_id is defined before submitting (form validation should enforce this)
    if (configUpdateBody.product_id === undefined) {
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('errors.productId.empty'),
      })
      return
    }

    createCluster
      .mutateAsync({
        pathParams: {
          orgId: request._organizationId,
        },
        body: configUpdateBody as ClusterUpdate,
      })
      .then(() => {
        toaster.create({
          ...SUCCESS_TOAST_OPTION,
          title: t('success.createClusterConfig'),
        })
        return navigate({
          to: clustersRootRoute.to,
          search: { page: 1 } as { page: number },
        })
      })
      .catch((error) => {
        console.error('error posting', error)
        toaster.create({
          ...ERROR_TOAST_OPTION,
          title: t('errors.submitConfig'),
          description: `${error.message}: ${error.stack._embedded.errors[0].message}`,
        })
      })
  }

  const steps = [
    {
      title: t('steps.step1.title'),
      description: t('steps.step1.description'),
      longerDescription: t('steps.step1.longerDescription'),
    },
    {
      title: t('steps.step2.title'),
      description: t('steps.step2.description'),
      longerDescription: t('steps.step2.longerDescription'),
    },
    {
      title: t('steps.step3.title'),
      description: t('steps.step3.description'),
      longerDescription: t('steps.step3.longerDescription'),
      optional: true,
    },
    {
      title: t('steps.step4.title'),
      description: t('steps.step4.description'),
      longerDescription: t('steps.step4.longerDescription'),
    },
  ]

  const breadCrumbs = [
    { name: t('sidebar.v3ClusterRevisions'), path: clusterConfigTableRoute.to },
    {
      name: 'New Hive',
      path: '',
    },
  ]

  function nextStep() {
    if (currentStep === StepIndex.STEP1_SYSTEM) {
      validateStep1AndNavigate(StepIndex.STEP2_EXTENSIONS)
      return
    }

    // check that all the extensions are valid
    if (currentStep === StepIndex.STEP2_EXTENSIONS) {
      // Extension validation will be handled in each extension's submit handler
      // No need to check showKafkaConfig here anymore
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  function prevStep() {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const completeStep1ErrorToast = 'complete-step-1-error-toast'

  // Function to validate step 1 and navigate to the specified step if valid
  const validateStep1AndNavigate = React.useCallback(
    (targetStep: StepIndex) => {
      if (currentStep !== targetStep) {
        // Validate step 1 before allowing navigation to other steps
        if (targetStep !== StepIndex.STEP1_SYSTEM) {
          const formIsValid = stepOneRef.current?.validateForm()
          if (!formIsValid) {
            const toastContent = {
              ...ERROR_TOAST_OPTION,
              title: t('errors.completeStep1.title'),
              description: t('errors.completeStep1.description'),
            }
            // Form is invalid, show a toast message
            showOrUpdateToast(completeStep1ErrorToast, toastContent)
            // Navigate to step 1 to fix errors
            setCurrentStep(StepIndex.STEP1_SYSTEM)
            return false
          }
        }
        // Form is valid or we're navigating to step 1, proceed
        setCurrentStep(targetStep)
        return true
      }
      return false
    },
    [currentStep, t],
  )

  // Helper to focus a field by id or selector
  const focusField = React.useCallback((field: string) => {
    // Map field names to selectors/ids
    const fieldMap: Record<string, () => void> = {
      name: () => document.getElementById('name')?.focus?.(),
      organizationId: () =>
        document.getElementById('organization-id')?.focus?.(),
      baseTemplate: () => {
        // For Select components, we need to find and click the trigger button
        const templateField = document.querySelector(
          '[data-field="template-id"] button',
        ) as HTMLElement
        templateField?.focus?.()
        templateField?.click?.()
      },
      productId: () => {
        const productField = document.querySelector(
          '[data-field="product-id"] button',
        ) as HTMLElement
        productField?.focus?.()
        productField?.click?.()
      },
      deploymentLabel: () => {
        const deploymentField = document.querySelector(
          '[data-field="deployment-label"] button',
        ) as HTMLElement
        deploymentField?.focus?.()
        deploymentField?.click?.()
      },
      changelog: () => document.getElementById('changelog')?.focus?.(),
      overridesEditor: () => setFocusOverrideEditor(true),
    }
    if (fieldMap[field]) {
      fieldMap[field]()
    }
  }, [])

  // Focus after step change
  React.useEffect(() => {
    if (pendingFocus && currentStep === pendingFocus.step) {
      setTimeout(() => focusField(pendingFocus.field), 0)

      // Don't clear pendingFocus if we're at step 2 and focusing on kafka or kinesis
      // This allows the tab selection to work correctly
      if (
        currentStep !== StepIndex.STEP2_EXTENSIONS ||
        (pendingFocus.field !== ExtensionType.KAFKA &&
          pendingFocus.field !== ExtensionType.KINESIS)
      ) {
        setPendingFocus(null)
      }
    }
  }, [currentStep, pendingFocus, focusField])

  // Reset focus override editor flag after it's been used
  React.useEffect(() => {
    if (focusOverrideEditor && currentStep === StepIndex.STEP3_OVERRIDES) {
      setTimeout(() => {
        setFocusOverrideEditor(false)
      }, DELAY_BEFORE_FOCUS)
    }
  }, [focusOverrideEditor, currentStep])

  function getStep1FieldStatus(
    field: keyof typeof clusterStep1Schema.shape,
    value: unknown,
  ): StepNavigatorItemStatus {
    if (value === undefined || value === '' || value === null) {
      return StepNavigatorItemStatus.NotConfigured
    }
    return clusterStep1Schema.shape[field]?.safeParse(value).success
      ? StepNavigatorItemStatus.Valid
      : StepNavigatorItemStatus.Invalid
  }

  // StepNavigator items for each step
  const step1Items = [
    {
      label: t('fields.name'),
      status: getStep1FieldStatus('_clusterName', request._clusterName),
      onClick: () => {
        if (currentStep === StepIndex.STEP1_SYSTEM) {
          focusField('name')
        } else {
          setPendingFocus({ step: StepIndex.STEP1_SYSTEM, field: 'name' })
          setCurrentStep(StepIndex.STEP1_SYSTEM)
        }
      },
    },
    {
      label: t('fields.orgId'),
      status: getStep1FieldStatus('_organizationId', request._organizationId),
      onClick: () => {
        if (currentStep === StepIndex.STEP1_SYSTEM) {
          focusField('organizationId')
        } else {
          setPendingFocus({
            step: StepIndex.STEP1_SYSTEM,
            field: 'organizationId',
          })
          setCurrentStep(StepIndex.STEP1_SYSTEM)
        }
      },
    },
    {
      label: t('fields.templateId'),
      status: getStep1FieldStatus('template_id', request.template_id),
      onClick: () => {
        if (currentStep === StepIndex.STEP1_SYSTEM) {
          focusField('baseTemplate')
        } else {
          setPendingFocus({
            step: StepIndex.STEP1_SYSTEM,
            field: 'baseTemplate',
          })
          setCurrentStep(StepIndex.STEP1_SYSTEM)
        }
      },
    },
    {
      label: t('fields.productId'),
      status: getStep1FieldStatus('product_id', request.product_id),
      onClick: () => {
        if (currentStep === StepIndex.STEP1_SYSTEM) {
          focusField('productId')
        } else {
          setPendingFocus({ step: StepIndex.STEP1_SYSTEM, field: 'productId' })
          setCurrentStep(StepIndex.STEP1_SYSTEM)
        }
      },
    },
    {
      label: t('fields.deploymentLabel'),
      status: getStep1FieldStatus('_deploymentLabel', request._deploymentLabel),
      onClick: () => {
        if (currentStep === StepIndex.STEP1_SYSTEM) {
          focusField('deploymentLabel')
        } else {
          setPendingFocus({
            step: StepIndex.STEP1_SYSTEM,
            field: 'deploymentLabel',
          })
          setCurrentStep(StepIndex.STEP1_SYSTEM)
        }
      },
    },
  ]

  const step2Items = [
    {
      label: t('fields.authentication'),
      status: StepNavigatorItemStatus.NotConfiguredOptional,
      onClick: () => undefined,
      disabled: true,
    },
    {
      label: t('fields.extensions'),
      status:
        request._kafkaExtension || request._kinesisExtension
          ? StepNavigatorItemStatus.Valid
          : StepNavigatorItemStatus.NotConfiguredOptional,
      onClick: () => {
        if (currentStep === StepIndex.STEP1_SYSTEM) {
          validateStep1AndNavigate(StepIndex.STEP2_EXTENSIONS)
          return
        }
        setCurrentStep(StepIndex.STEP2_EXTENSIONS)
      },
      disabled: false,
      children: [
        {
          label: t('fields.kafka'),
          status: request._kafkaExtension
            ? StepNavigatorItemStatus.Valid
            : StepNavigatorItemStatus.NotConfiguredOptional,
          onClick: () => {
            // Set focus to Kafka extension tab
            setPendingFocus({
              step: StepIndex.STEP2_EXTENSIONS,
              field: ExtensionType.KAFKA,
            })
            if (currentStep === StepIndex.STEP1_SYSTEM) {
              validateStep1AndNavigate(StepIndex.STEP2_EXTENSIONS)
              return
            }
            setCurrentStep(StepIndex.STEP2_EXTENSIONS)
          },
        },
        {
          label: t('fields.kinesis'),
          status: request._kinesisExtension
            ? StepNavigatorItemStatus.Valid
            : StepNavigatorItemStatus.NotConfiguredOptional,
          onClick: () => {
            // Set focus to Kinesis extension tab
            setPendingFocus({
              step: StepIndex.STEP2_EXTENSIONS,
              field: ExtensionType.KINESIS,
            })
            if (currentStep === StepIndex.STEP1_SYSTEM) {
              validateStep1AndNavigate(StepIndex.STEP2_EXTENSIONS)
              return
            }
            setCurrentStep(StepIndex.STEP2_EXTENSIONS)
          },
        },
      ],
    },
  ]

  const step3Items = [
    {
      label: t('fields.variables'),
      status: request._overrideYaml
        ? StepNavigatorItemStatus.Valid
        : StepNavigatorItemStatus.NotConfiguredOptional,
      onClick: () => {
        // Set flag to focus the editor when it's rendered
        setFocusOverrideEditor(true)

        if (currentStep === StepIndex.STEP1_SYSTEM) {
          validateStep1AndNavigate(StepIndex.STEP3_OVERRIDES)
          return
        }
        setCurrentStep(StepIndex.STEP3_OVERRIDES)
      },
    },
  ]

  const step4Items = [
    {
      label: t('fields.comments'),
      status: request.changelog
        ? StepNavigatorItemStatus.Valid
        : StepNavigatorItemStatus.NotConfigured,
      onClick: () => {
        // if coming from step 1, validate and navigate to step 4
        if (currentStep === StepIndex.STEP1_SYSTEM) {
          setPendingFocus({ step: StepIndex.STEP4_REVIEW, field: 'changelog' })
          validateStep1AndNavigate(StepIndex.STEP4_REVIEW)
          return
        }

        // otherwise just set the step
        setPendingFocus({ step: StepIndex.STEP4_REVIEW, field: 'changelog' })
        setCurrentStep(StepIndex.STEP4_REVIEW)
      },
    },
  ]

  const stepNavigatorItems = [step1Items, step2Items, step3Items, step4Items]

  return (
    <PageLayout
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      title={t('pageTitle.createClusterConfig')}
    >
      <VStack width="100%" gap={8}>
        <Steps.Root
          step={currentStep}
          count={steps.length}
          width="100%"
          alignItems="normal"
          colorPalette="blue"
        >
          <Steps.List alignItems="baseline">
            {steps.map((step, idx) => (
              <VStack
                key={`config-stepper-${step.title}`}
                gap={4}
                flex="1"
                align="stretch"
              >
                <Steps.Item index={idx}>
                  <Steps.Indicator />
                  <Box flexShrink="0">
                    <Steps.Title>{step.title}</Steps.Title>
                    <Steps.Description>{step.description}</Steps.Description>
                  </Box>
                  <Steps.Separator />
                </Steps.Item>
                <Box ml="40px">
                  <StepNavigator
                    minHeight="48px"
                    items={stepNavigatorItems[idx]}
                  />
                </Box>
              </VStack>
            ))}
          </Steps.List>
        </Steps.Root>

        <VStack w="100%" alignItems="start" gap={4}>
          <HStack
            w="100%"
            alignItems="center"
            justifyContent="space-between"
            gap={4}
          >
            <HStack gap={4}>
              <Heading size="3xl">{steps[currentStep].title}</Heading>
              {Boolean(steps[currentStep].optional) && (
                <Tag.Root variant="outline" fontSize="xs">
                  <Tag.Label>{t('fields.optional')}</Tag.Label>
                </Tag.Root>
              )}
            </HStack>
            <HStack gap={4}>
              <Button
                onClick={prevStep}
                disabled={currentStep === StepIndex.STEP1_SYSTEM}
                variant="outline"
                width="7rem"
              >
                {t('actions.previous')}
              </Button>

              {currentStep === steps.length - 1 ? (
                <Button
                  variant="solid"
                  disabled={!request.changelog?.length}
                  onClick={submit}
                  width="7rem"
                >
                  <Icon as={Check} />
                  {t('actions.submit')}
                </Button>
              ) : (
                <Button onClick={nextStep} colorPalette="brand" width="7rem">
                  {t('actions.continue')}
                </Button>
              )}
            </HStack>
          </HStack>
          <Text color="text.text-subtle">
            {steps[currentStep].longerDescription}
          </Text>
        </VStack>

        <Box width="100%" alignSelf="start">
          {currentStep === StepIndex.STEP1_SYSTEM && (
            <TemplatesProvider>
              <NewClusterStep1System ref={stepOneRef} />
            </TemplatesProvider>
          )}

          {currentStep === StepIndex.STEP2_EXTENSIONS && (
            <NewClusterStep2Features
              configUpdateBody={configUpdateBody}
              onChange={onChange}
              initialExtensionTab={pendingFocus?.field as ExtensionType}
            />
          )}

          {currentStep === StepIndex.STEP3_OVERRIDES && (
            <NewClusterStep3Overrides
              ref={step3OverridesRef}
              overrideYaml={request._overrideYaml ?? ''}
              onChange={(value) => onChange('_overrideYaml', value)}
              shouldFocus={focusOverrideEditor}
            />
          )}

          {currentStep === StepIndex.STEP4_REVIEW && (
            <NewClusterStep4Review
              changelog={request.changelog ?? ''}
              onChange={(value) => onChange('changelog', value)}
            />
          )}
        </Box>

        {debugModeAvailable && !showDebug && (
          <Button
            size="sm"
            backgroundColor="yellow.600"
            _hover={{ backgroundColor: 'yellow.700' }}
            color="text.text-on-color"
            onClick={() => setShowDebug(true)}
          >
            {t('actions.showDebug')}
          </Button>
        )}

        {showDebug && (
          <VStack
            data-testid="debug"
            display="flex"
            justifyContent="flex-end"
            border=".0625rem solid"
            borderColor="yellow.600"
            borderRadius={4}
            alignItems="start"
            gap={0}
            my={4}
            w="100%"
            fontSize="sm"
          >
            <HStack
              p={2}
              width="100%"
              backgroundColor="yellow.600"
              justifyContent="space-between"
            >
              <VStack alignItems="start" gap={0}>
                <Text color="text.text-on-color" fontSize="sm" fontWeight="500">
                  {t('debug.title')}
                </Text>
                <Text
                  color="text.text-on-color"
                  fontWeight={400}
                  fontStyle="italic"
                >
                  {t('debug.yamlDescription')}
                </Text>
              </VStack>
              <HStack>
                <Tag.Root colorPalette={configIsValid ? 'green' : 'red'}>
                  <Tag.Label>
                    {t(`debug.${configIsValid ? 'valid' : 'invalid'}`)}
                  </Tag.Label>
                </Tag.Root>
                <IconButton
                  onClick={() => setShowDebug(false)}
                  aria-label="hide debug"
                  variant="ghost"
                  minWidth="24px"
                  height="24px"
                  border={0}
                  color="icon.icon-on-color"
                  _hover={{ backgroundColor: 'transparent' }}
                >
                  <Icon as={X} w={6} h={6} />
                </IconButton>
              </HStack>
            </HStack>
            {Boolean(validationError?.length) && (
              <Box backgroundColor="background.bg-error" width="100%" p={2}>
                <Text>{validationError}</Text>
              </Box>
            )}
            <Box p={4}>
              <Tabs.Root defaultValue="yaml">
                <Tabs.List>
                  <Tabs.Trigger value="yaml">{t('debug.yaml')}</Tabs.Trigger>
                  <Tabs.Trigger value="json">{t('debug.json')}</Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="yaml">
                  <pre>organizationId: {request._organizationId}</pre>
                  <pre>{yamlRequest}</pre>
                </Tabs.Content>
                <Tabs.Content value="json">
                  <pre>{JSON.stringify(configUpdateBody, null, 2)}</pre>
                </Tabs.Content>
              </Tabs.Root>
            </Box>
          </VStack>
        )}
      </VStack>
    </PageLayout>
  )
}
